import { useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDiscoveredEntities, UnauthorizedError } from '../../api';
import { useSettings } from '../../settings';
import { AccountSettings } from './AccountSettings';
import { CostSettingsForm } from './CostSettingsForm';
import { DevelopersSettings } from './DevelopersSettings';
import { DataHealthSettingsForm } from './DataHealthSettingsForm';
import { ExecutiveSettingsForm } from './ExecutiveSettingsForm';
import { FeatureFlagsSettings } from './FeatureFlagsSettings';
import { HistorySettings } from './HistorySettings';
import { ModelPricingSettings } from './ModelPricingSettings';
import { TeamsSettings } from './TeamsSettings';
import { TargetsSettingsForm } from './TargetsSettingsForm';
import { focusRing } from './formParts';

type TabId = 'developers' | 'teams' | 'models' | 'cost' | 'targets' | 'executive' | 'features' | 'dataHealth' | 'history' | 'account';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'developers', label: 'Developers', icon: 'fa-solid fa-user-tag' },
  { id: 'teams', label: 'Teams', icon: 'fa-solid fa-people-group' },
  { id: 'models', label: 'Model pricing', icon: 'fa-solid fa-tags' },
  { id: 'cost', label: 'Cost & currency', icon: 'fa-solid fa-coins' },
  { id: 'targets', label: 'Budgets & goals', icon: 'fa-solid fa-bullseye' },
  { id: 'executive', label: 'Executive', icon: 'fa-solid fa-chart-line' },
  { id: 'features', label: 'Feature flags', icon: 'fa-solid fa-toggle-on' },
  { id: 'dataHealth', label: 'Data health', icon: 'fa-solid fa-heart-pulse' },
  { id: 'history', label: 'History', icon: 'fa-solid fa-clock-rotate-left' },
  { id: 'account', label: 'Account', icon: 'fa-solid fa-user-gear' },
];

/**
 * Admin settings. Every tab stays mounted (inactive ones are `hidden`) so
 * unsaved edits survive switching tabs; each tab saves independently.
 */
export function SettingsPage() {
  const [tab, setTab] = useState<TabId>('developers');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { data: settings, isLoading, isError, error } = useSettings();
  const discovery = useQuery({
    queryKey: ['settings-discovered'],
    queryFn: fetchDiscoveredEntities,
    staleTime: 10 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 1,
  });
  const discoveryError = discovery.isError ? (discovery.error as Error).message : undefined;

  // Tablist keyboard pattern: arrows move between tabs, Home/End jump.
  function onTabKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const i = TABS.findIndex((t) => t.id === tab);
    const next =
      e.key === 'ArrowRight' ? (i + 1) % TABS.length
      : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? TABS.length - 1
      : -1;
    if (next < 0) return;
    e.preventDefault();
    setTab(TABS[next].id);
    tabRefs.current[TABS[next].id]?.focus();
  }

  if (isLoading) return <p className="font-light text-text-muted">Loading settings…</p>;
  if (isError || !settings) {
    return <p className="font-medium text-error">Failed to load settings: {(error as Error | null)?.message ?? 'unknown error'}</p>;
  }

  return (
    <div>
      <div role="tablist" aria-label="Settings sections" className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              ref={(el) => (tabRefs.current[t.id] = el)}
              type="button"
              role="tab"
              id={`settings-tab-${t.id}`}
              aria-selected={active}
              aria-controls={`settings-panel-${t.id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setTab(t.id)}
              onKeyDown={onTabKeyDown}
              className={`relative -mb-px flex min-h-[44px] items-center gap-2 border-b-[3px] px-4 text-sm transition-colors duration-150 ${focusRing} ${
                active
                  ? 'border-primary-blue font-bold text-primary-blue'
                  : 'border-transparent text-text-muted hover:text-text-default'
              }`}
            >
              <i aria-hidden="true" className={t.icon} />
              {t.label}
            </button>
          );
        })}
      </div>

      {TABS.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`settings-panel-${t.id}`}
          aria-labelledby={`settings-tab-${t.id}`}
          hidden={t.id !== tab}
          className="pt-6"
        >
          {t.id === 'developers' && (
            <DevelopersSettings settings={settings} discovered={discovery.data} discoveryError={discoveryError} />
          )}
          {t.id === 'teams' && <TeamsSettings settings={settings} />}
          {t.id === 'models' && (
            <ModelPricingSettings settings={settings} discovered={discovery.data} discoveryError={discoveryError} />
          )}
          {t.id === 'cost' && <CostSettingsForm settings={settings} />}
          {t.id === 'executive' && <ExecutiveSettingsForm settings={settings} />}
          {t.id === 'features' && <FeatureFlagsSettings settings={settings} />}
          {t.id === 'dataHealth' && <DataHealthSettingsForm settings={settings} />}
          {t.id === 'targets' && <TargetsSettingsForm settings={settings} />}
          {t.id === 'history' && <HistorySettings />}
          {t.id === 'account' && <AccountSettings />}
        </div>
      ))}
    </div>
  );
}
